import React, { useState, useRef, useEffect } from 'react';
import { COUNTRY_CODES } from '../../constants/countryCodes';

interface CountryCodeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  error?: boolean;
}

const CountryCodeDropdown: React.FC<CountryCodeDropdownProps> = ({
  value,
  onChange,
  className = "",
  style = {},
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountryState, setSelectedCountryState] = useState<{code: string, country: string, flag: string} | null>(null);
  const [showAbove, setShowAbove] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  // Initialize selected country from value
  useEffect(() => {
    const countriesWithSameCode = COUNTRY_CODES.filter(country => country.code === value);
    const country = countriesWithSameCode.find(c => c.country === 'US') || 
                   countriesWithSameCode[0] || 
                   COUNTRY_CODES[0];
    setSelectedCountryState({ code: country.code, country: country.country, flag: country.flag });
  }, [value]);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250; // Approximate dropdown height
      
      setShowAbove(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight);
    }
  }, [isOpen]);

  // Auto-scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && selectedItemRef.current && listContainerRef.current) {
      setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 50);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedCountry = selectedCountryState || COUNTRY_CODES[0];

  const handleSelect = (countryCode: string, countryLabel: string, flag: string) => {
    setSelectedCountryState({ code: countryCode, country: countryLabel, flag });
    onChange(countryCode);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Filter and sort countries based on search query
  const filteredCountries = COUNTRY_CODES
    .filter(country => {
      const searchLower = searchQuery.toLowerCase().trim();
      if (!searchLower) return true;
      
      return country.code.toLowerCase().includes(searchLower) || 
             country.country.toLowerCase().includes(searchLower) ||
             country.name.toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      // Primary sort: by code (numeric)
      const codeA = parseInt(a.code.replace('+', '')) || 0;
      const codeB = parseInt(b.code.replace('+', '')) || 0;
      
      if (codeA !== codeB) {
        return codeA - codeB;
      }
      
      // If codes are the same, sort by country
      return a.country.localeCompare(b.country);
    });

  return (
    <div className={`relative ${className}`} ref={dropdownRef} style={style}>
      {/* Selected Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full border-b ${
          error ? 'border-red-500' : 'border-gray-300'
        } px-0 py-2 text-sm focus:outline-none font-family-messina-sans-regular flex-shrink-0 flex items-center justify-between bg-transparent transition-colors hover:border-gray-400`}
        style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedCountry.flag}</span>
          <span>{selectedCountry.code}</span>
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Options */}
      {isOpen && (
        <div className={`absolute left-0 z-50 bg-white border border-gray-300 rounded-lg shadow-xl min-w-[280px] ${showAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          {/* Search Input */}
          <div className="p-2 pb-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search country code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 font-family-messina-sans"
              autoFocus
            />
          </div>
          
          {/* Country List */}
          <div ref={listContainerRef} className="max-h-[200px] overflow-y-auto pb-2">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={`${country.code}-${country.country}`}
                  ref={country.code === selectedCountry.code && country.country === selectedCountry.country ? selectedItemRef : null}
                  type="button"
                  onClick={() => handleSelect(country.code, country.country, country.flag)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-3 transition-colors ${
                    country.code === selectedCountry.code && country.country === selectedCountry.country ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="flex-1 font-medium font-family-messina-sans">{country.code}</span>
                  <span className="text-gray-600 text-xs font-family-messina-sans">{country.country}</span>
                  {country.code === selectedCountry.code && country.country === selectedCountry.country && (
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeDropdown;
